import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { default_Photo, playerTeamLogo } from "../../../Assets";
import { useAuthStore } from "../../../store";

const BASE_PRICES = {
  Icon: 2000000,
  Senior: 500000,
  Emerging: 300000,
  Development: 200000,
};

const MINIMUM_REQUIRED = {
  Icon: 1,
  Senior: 4,
  Emerging: 6,
  Development: 5,
};

const TOTAL_REQUIRED_PLAYERS = 18;

const getKittyColor = (kittyLeft) => {
  if (kittyLeft >= 2800000) return "transparent";
  if (kittyLeft >= 2000000) return "#FFF176"; // mid yellow
  if (kittyLeft >= 1200000) return "#FFF9C4"; // light yellow
  if (kittyLeft >= 400000) return "#FFCDD2"; // light red
  return "#F44336"; // red
};
const computeRemainingKitty = (team) => {
  const minSpend =
    MINIMUM_REQUIRED.Icon * BASE_PRICES.Icon +
    MINIMUM_REQUIRED.Senior * BASE_PRICES.Senior +
    MINIMUM_REQUIRED.Emerging * BASE_PRICES.Emerging +
    MINIMUM_REQUIRED.Development * BASE_PRICES.Development +
    2 * BASE_PRICES.Development;
  return 10000000 - minSpend - (team.totalSpent || 0);
};

const BidPriceModal = ({
  open,
  onClose,
  player,
  bid,
  setBid,
  onFinalize,
  onUnsold,
  teams,
  selectedTeam,
  setSelectedTeam,
  onConfirm,
  validateTeam,
  teamdetailsWithStatus,
}) => {
  const [confirmUnsold, setConfirmUnsold] = React.useState(false);
  const [bidTouched, setBidTouched] = React.useState(false);
  const [hasIncremented, setHasIncremented] = React.useState(false);
  // const [debouncedInput, setDebouncedInput] = useState(bid);
  const basePrice = player?.basePrice || 0;

  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     if (!isNaN(debouncedInput)) {
  //       teamdetailsWithStatus(debouncedInput, player); // Debounced
  //     }
  //   }, 300);

  //   return () => clearTimeout(timeout);
  // }, [debouncedInput, player]);

  // For increments
  const getIncrementValue = (bid) => {
    if (bid >= 500000) {
      return 25000;
    } else if (bid >= 300000) {
      return 20000;
    } else {
      return 10000;
    }
  };

  // For decrements
  const getDecrementValue = (bid) => {
    if (bid > 500000) {
      return 25000;
    } else if (bid > 300000) {
      return 20000;
    } else {
      return 10000;
    }
  };

  const handleIncrement = async () => {
    if (!hasIncremented) {
      // First click: only send live bid update with current bid
      await useAuthStore.getState().sendLiveBidUpdate({
        player,
        bidAmount: bid,
        status: "liveBid",
      });
      setHasIncremented(true); // Mark that first click has occurred
      teamdetailsWithStatus(bid, player);
      return;
    }

    // Subsequent clicks: increment as usual
    const increment = getIncrementValue(bid);
    const newBid = bid + increment;
    setBid(newBid);
    setSelectedTeam(null);
    teamdetailsWithStatus(newBid, player);
    await useAuthStore.getState().sendLiveBidUpdate({
      player,
      bidAmount: newBid,
      status: "liveBid",
    });

    if (newBid > basePrice) {
      setBidTouched(true);
    }
  };

  const handleDecrement = async () => {
    const decrement = getDecrementValue(bid);
    const newBid = Math.max(0, bid - decrement);
    setBid(newBid);
    setSelectedTeam(null);
    teamdetailsWithStatus(newBid, player);
    await useAuthStore.getState().sendLiveBidUpdate({
      player,
      bidAmount: newBid,
      status: "liveBid",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        setBidTouched(false);
        setConfirmUnsold(false);
        setSelectedTeam(null);
        onClose();
      }}
      maxWidth="md"
      PaperProps={{
        sx: {
          background: "#0D0D0D",
          border: "0.5px solid rgba(255, 255, 255, 0.20)",
          borderRadius: "16px",
          p: "32px",
          width: "700px",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: "0px",
          pb: "12px",
          color: "#fff",
          fontWeight: "bold",
          fontSize: "20px",
          borderBottom: "1px solid #ffffff33",
        }}
      >
        {confirmUnsold ? "Mark as unsold?" : "Bid price"}
        <IconButton
          onClick={onClose}
          sx={{ float: "right", color: "#fff", p: "0px" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        mt={"36px"}
        m={"3vw"}
      >
        <Avatar
          src={player?.photo ? player?.photo : default_Photo}
          alt={player?.name}
          sx={{
            width: "124px",
            height: "124px",
            mb: 1,
            border: "0.5px solid #5C5C5C",
          }}
        />
        <Typography sx={{ fontWeight: 600, fontSize: "20px", color: "#fff" }}>
          {player?.name}
        </Typography>
        <Typography sx={{ color: "#bdbdbd", mb: 2 }}>
          {player?.MCANo}
        </Typography>

        {!confirmUnsold ? (
          <Box>
            <Box display="flex" gap={2} mt={1}>
              <IconButton
                onClick={handleDecrement}
                disabled={bid <= player?.basePrice}
                sx={{
                  height: "52px",
                  width: "54px",
                  border: "1px solid #797979",
                  color: "#fff",
                  borderRadius: "12px",
                  background: "#545454",
                  "&:hover": {
                    background: "#545454",
                  },
                  opacity: bid <= player?.basePrice ? 0.5 : 1,
                  cursor: bid <= player?.basePrice ? "not-allowed" : "pointer",
                }}
              >
                <RemoveIcon />
              </IconButton>
              <TextField
                type="text"
                value={bid.toLocaleString("en-IN")}
                inputProps={{
                  sx: {
                    textAlign: "center",
                    color: "#fff",
                    // "&.Mui-disabled": {
                    //   color: "#fff",
                    //   WebkitTextFillColor: "#fff",
                    // },
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "#545454",
                    boxShadow: "none !important",
                    height: "17px",
                    fontSize: "18px",
                    fontWeight: 600,
                    borderRadius: "12px",
                    border: "1px solid #797979",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    boxShadow: "none !important",
                  },
                  "& fieldset": { border: "none" },
                }}
                onChange={async (e) => {
                  let value = Number(e.target.value.replace(/[^0-9]/g, ""));
                  if (isNaN(value)) value = 0;
                  setBid(value);
                  teamdetailsWithStatus(value, player);
                  // setDebouncedInput(value);
                }}
              />
              <IconButton
                onClick={handleIncrement}
                sx={{
                  height: "52px",
                  width: "54px",
                  border: "1px solid #797979",
                  color: "#fff",
                  borderRadius: "12px",
                  background: "#545454",
                  "&:hover": {
                    background: "#545454",
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
            <Box>
              <Grid container spacing={2} mt={1}>
                {teams
                  ?.slice()
                  .sort((a, b) => a.Name.localeCompare(b.Name))
                  .map((team) => {
                    return (
                      <Grid item xs={12} md={3} key={team?.Name}>
                        <Box
                          onClick={() => {
                            if (validateTeam(team)) {
                              setSelectedTeam(team);
                            } else {
                              setSelectedTeam(team);
                            }
                          }}
                          sx={{
                            height: "4vw",
                            width: "4vw",
                            backgroundColor:
                              team?.teamStatus?.status === "No Fund"
                                ? "#cf1717d7"
                                : "transparent",
                            border:
                              selectedTeam?.Name === team?.Name
                                ? "1px solid #FF8C00"
                                : "1px solid rgba(255,255,255,0.5)",
                            borderRadius: "100%",
                            p: "1.4vw",
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={
                              team?.Name == "MSC Maratha Royals"
                                ? "https://mcadirectory.blob.core.windows.net/dev/4441894866960012-shivajiParkLions.png"
                                : team?.Logo_URL__c || playerTeamLogo
                            }
                            alt={team?.Name}
                            style={{ height: "5vw" }}
                          />
                          {/* <Typography
                          sx={{
                            mt: "2px",
                            fontWeight: 600,
                            color: "#fff",
                            fontSize: "0.7vw",
                          }}
                        >
                          {team?.teamStatus?.status}
                        </Typography> */}
                        </Box>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 500,
              textAlign: "center",
              color: "#fff",
            }}
          >
            Are you sure, you want to mark this player as unsold?
          </Typography>
        )}

        {confirmUnsold ? (
          <Box display="flex" width={"100%"} gap={2} mt={4}>
            <Button
              variant="outlined"
              onClick={() => setConfirmUnsold(false)}
              // onClick={onUnsold}
              sx={{
                height: "48px",
                borderColor: "#fff",
                color: "#fff",
                px: 3,
                borderRadius: "8px",
                flex: 1,
                fontSize: "16px",
                fontWeight: "500",
                background: "#0D0D0D",
                "&:hover": {
                  borderColor: "#fff",
                  background: "#0D0D0D",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onUnsold}
              sx={{
                height: "48px",
                background: "#fff",
                color: "#000",
                px: 3,
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "500",
                flex: 1,
                "&:hover": {
                  background: "#fff",
                },
              }}
            >
              Mark as unsold
            </Button>
          </Box>
        ) : (
          <Box display="flex" width={"100%"} gap={2} mt={4}>
            <Button
              variant="outlined"
              onClick={() => setConfirmUnsold(true)}
              disabled={bidTouched}
              // onClick={onUnsold}
              sx={{
                height: "48px",
                borderColor: "#fff",
                color: "#fff",
                px: 3,
                borderRadius: "8px",
                flex: 1,
                fontSize: "16px",
                fontWeight: "500",
                background: "#0D0D0D",
                "&:hover": {
                  borderColor: "#fff",
                  background: "#0D0D0D",
                },
                "&.Mui-disabled": {
                  color: "#fff",
                  borderColor: "#fff",
                  opacity: 0.5,
                },
              }}
            >
              Mark as unsold
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (player?.recentCompetetionLevel === "Indian senior team") {
                  onFinalize();
                  return;
                }
                if (!bidTouched) {
                  await useAuthStore.getState().sendLiveBidUpdate({
                    player,
                    bidAmount: player?.basePrice,
                    status: "liveBid",
                  });

                  setBidTouched(true);
                } else {
                  onConfirm();
                }
              }}
              // disabled={!selectedTeam || bid < player?.basePrice}
              sx={{
                height: "48px",
                background: "#fff",
                color: "#000",
                px: 3,
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "500",
                flex: 1,
                "&:hover": {
                  background: "#fff",
                },
              }}
            >
              Finalize bid
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default BidPriceModal;
